import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import { Button } from "../components/ui/Button.js";
import { Input } from "../components/ui/Input.js";
import { Card } from "../components/ui/Card.js";
import { Badge } from "../components/ui/Badge.js";
import { useToast } from "../hooks/use-toast.js";
import { useMutation } from "@apollo/client";
import { ADD_WAITLIST_ENTRY } from "../lib/graphqlOperations.js";
import { Navbar } from "../components/layout/Navbar.js";
import Footer from "../components/layout/Footer.js";
import vendorShop from "../assets/stock_images/vendorShop.png";
import {
  Store,
  Phone,
  Check,
  ArrowRight,
  Gift,
  Zap,
  Clock,
  Banknote,
  Share2,
  Copy,
  Bell,
  TrendingUp,
  ShieldCheck,
  Package,
  Users,
} from "lucide-react";

const benefits = [
  {
    icon: Phone,
    title: "Sign Up with Phone",
    description: "Just your phone number. That's all you need to get started.",
  },
  {
    icon: Zap,
    title: "Zero Setup Required",
    description: "No technical skills needed. No website to build. We handle everything.",
  },
  {
    icon: Bell,
    title: "Get Order Notifications",
    description: "Receive orders instantly. Accept with one tap and start fulfilling.",
  },
  {
    icon: Banknote,
    title: "Get Paid Fast",
    description: "Quick payouts directly to your bank. No waiting, no hassle.",
  },
];

const howItWorks = [
  {
    step: 1,
    title: "Sign Up",
    description: "Enter your phone number and business name. Done in 30 seconds.",
  },
  {
    step: 2,
    title: "Accept Orders",
    description: "Get notified when orders come in. Accept and prepare the order.",
  },
  {
    step: 3,
    title: "We Deliver",
    description: "Our delivery partners pick up and deliver to customers. Magic happens behind the scenes.",
  },
];

export default function Vendors() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showReferral, setShowReferral] = useState(false);
  const [myReferralCode, setMyReferralCode] = useState("");
  const { toast } = useToast();

  const [addWaitlistEntry, { loading: joinPending }] = useMutation(ADD_WAITLIST_ENTRY, {
    onCompleted: (data) => {
      if (data?.addWaitlistEntry?.success) {
        toast({
          title: "Welcome to Quro!",
          description: "You're signed up as a vendor. Free setup included!",
        });
        // Use id as a simple referral code placeholder until backend returns one
        setMyReferralCode(data.addWaitlistEntry.data?.id?.toString() || "");
        setShowReferral(true);
        setName("");
        setPhone("");
        setBusinessName("");
      } else {
        const message = data?.addWaitlistEntry?.message || "Something went wrong. Please try again.";
        toast({
          title: "Something went wrong",
          description: message,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Something went wrong",
        description: error.message || "Please enter a valid name and phone number.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name && phone) {
      const cleanPhone = phone.replace(/\s+/g, "").replace(/\D/g, "").replace(/^(\+?91|0)/, "").slice(-10);
      if (cleanPhone.length !== 10) {
        toast({
          title: "Invalid Phone",
          description: "Please enter a valid 10-digit phone number.",
          variant: "destructive",
        });
        return;
      }
      addWaitlistEntry({
        variables: {
          name,
          phone: cleanPhone,
          entryType: "vendor",
          message: businessName || undefined,
          location: referralCode || undefined,
        },
      });
    }
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(myReferralCode);
    toast({
      title: "Copied!",
      description: "Referral code copied to clipboard.",
    });
  };


  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 80, 0],
            scale: [1, 1.3, 1],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-primary/5 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{
            x: [0, 80, 0],
            y: [0, -50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-[10%] -left-[5%] w-[40%] h-[40%] bg-accent/10 rounded-full blur-[120px]"
        />
      </div>

      <div className="relative z-10">
        <section id="vendor-form" className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <Badge variant="secondary" className="px-4 py-2">
                <Gift className="w-4 h-4 mr-2" />
                Free Setup Worth 5,000 - Limited Time
              </Badge>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight">
                Start Selling Online.{" "}
                <span className="text-primary">No Setup Required.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                Just sign up with your phone and start accepting orders. We handle delivery,
                payments, and customer service. You focus on what you do best.
              </p>

              {!showReferral ? (
                <motion.form 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  onSubmit={handleSubmit} 
                  className="space-y-4 max-w-md relative"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="flex-1 h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md focus-visible:ring-primary/50 shadow-soft"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="flex-1 h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md focus-visible:ring-primary/50 shadow-soft"
                    />
                  </div>
                  <Input
                    type="text"
                    placeholder="Business name (optional)"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    className="h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md shadow-soft"
                  />
                  <Input
                    type="text"
                    placeholder="Referral code (optional)"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    className="h-14 rounded-2xl border-border/60 bg-background/50 backdrop-blur-md shadow-soft"
                  />
                  <Button
                    id="join-vendor-submit"
                    type="submit"
                    size="lg"
                    className="w-full h-14 rounded-2xl text-lg font-bold shadow-glow shadow-primary/30 hover:shadow-primary/50 transition-all duration-300 hover:-translate-y-1"
                    disabled={joinPending || !name || !phone}
                  >
                    {joinPending ? "Signing up..." : "Start Selling - Free Setup"}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.form>
              ) : (
                <Card className="p-6 max-w-md bg-primary/5 border-primary/20">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-primary">
                      <Check className="w-5 h-5" />
                      <span className="font-semibold">You're signed up!</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Share your referral code and earn 1,000 bonus for each vendor who joins!
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 p-3 bg-background rounded-md border font-mono text-lg font-bold text-center">
                        {myReferralCode}
                      </div>
                      <Button size="icon" variant="outline" onClick={copyReferralCode}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <Button variant="outline" className="w-full">
                      <Share2 className="w-4 h-4 mr-2" />
                      Share with other vendors
                    </Button>
                  </div>
                </Card>
              )}

              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span>Free setup included</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-primary" />
                  <span>Go live in minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-primary" />
                  <span>0% platform fee at launch</span>
                </div>
              </div>
            </div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/10 group">
                <img
                  src={vendorShop}
                  alt="Indian local vendor managing their shop"
                  className="w-full h-[500px] lg:h-[650px] object-cover transition-transform duration-1000 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                <div className="absolute bottom-8 left-8">
                  <Badge variant="secondary" className="bg-white/20 hover:bg-white/30 text-white backdrop-blur-md border border-white/10 px-4 py-2 text-sm">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Empowering Local Commerce
                  </Badge>
                </div>
              </div>
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-primary/20 rounded-full blur-[80px]" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-accent/20 rounded-full blur-[80px]" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              Why Vendors Love Quro
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Everything you need to sell online, without the complexity.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-10 rounded-[2.5rem] bg-card/40 backdrop-blur-xl border border-border/50 shadow-soft hover:-translate-y-2 transition-all duration-500 group overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-8 -mt-8 transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-10 right-10 text-6xl font-display font-black text-muted-foreground/10 select-none group-hover:text-primary/10 transition-colors">
                  0{index + 1}
                </div>
                <div className="relative w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 shadow-inner group-hover:bg-primary group-hover:text-white transition-colors duration-500">
                  <benefit.icon className="w-10 h-10 transition-transform group-hover:scale-110" />
                </div>
                <h3 className="relative font-display text-2xl font-bold mb-4 tracking-tight">{benefit.title}</h3>
                <p className="relative text-muted-foreground text-lg leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              From signup to your first sale in minutes. Here's how simple it is.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative p-10 rounded-[2.5rem] bg-card/40 backdrop-blur-xl border border-border/50 shadow-soft hover:-translate-y-3 transition-all duration-500 overflow-hidden group"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-bl-full -mr-8 -mt-8 transition-transform duration-500 group-hover:scale-125" />
                <div className="relative w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mb-8 shadow-inner group-hover:bg-primary group-hover:text-white transition-colors duration-500">
                  <span className="text-2xl font-black">{index + 1}</span>
                </div>
                <div className="absolute top-10 right-10 text-6xl font-display font-black text-muted-foreground/10 select-none group-hover:text-primary/10 transition-colors">
                  0{index + 1}
                </div>
                <h3 className="relative font-display text-2xl font-bold mb-4 tracking-tight">{step.title}</h3>
                <p className="relative text-muted-foreground text-lg leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
      <section id="referral" className="py-16 md:py-24 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
              <Gift className="w-4 h-4 mr-2" />
              Referral Program
            </Badge>
            <h2 className="font-display text-3xl md:text-5xl font-bold">
              Earn 1,000 for Every Vendor You Refer
            </h2>
            <p className="text-lg md:text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              Know other local vendors? Refer them to Quro and earn 1,000 bonus when they
              join and start selling. No limits on how many you can refer!
            </p>
            <div className="flex flex-wrap justify-center gap-8 py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <Share2 className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">Share Your Code</p>
                  <p className="text-sm text-primary-foreground/60">Get unique referral code</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">They Join & Sell</p>
                  <p className="text-sm text-primary-foreground/60">When they make first sale</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <Banknote className="w-6 h-6" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">You Earn 1,000</p>
                  <p className="text-sm text-primary-foreground/60">Credited to your account</p>
                </div>
              </div>
            </div>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              Sign Up to Get Your Referral Code
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            Ready to Grow Your Business?
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Join local vendors already selling on Quro. Free setup, no technical skills needed,
            and you can start accepting orders today.
          </p>
          <Button
            size="lg"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            Start Selling Now - It's Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
      </div>
    </div>
  );
}
